/**
 * Settings → Certifications: the one additional employee who may approve certifications.
 *
 * ── Stored in OrgConfig, not in a table of its own ────────────────────────────────────────
 *
 * This is a single configuration value the organisation shares, which is exactly what OrgConfig
 * already is — the same store behind Teams, Job Titles, Employment Types and the holiday
 * calendar. Its AppSync resolvers key on `configKey` and are agnostic about what the value
 * holds, so nothing on the backend changes to add this. A dedicated table would be one table,
 * two resolvers and a Terraform change to hold one email address.
 *
 * ── What the grant is, and what it is emphatically not ────────────────────────────────────
 *
 * Naming somebody here lets them approve or reject CERTIFICATION reimbursements. It does not
 * change their role, and it gives them nothing else: not other expense queues, not reports, not
 * exports, not Settings. See canApproveCertifications for how that stays true — the address is
 * checked alongside the role rather than being turned into one.
 *
 * The existing finance and super admins keep their access regardless of what is configured here.
 * This approver is additional; removing the configuration removes only their access.
 */

import { generateClient } from 'aws-amplify/api';
import { canonicalizeEmail } from './identity';

/** The OrgConfig document id. Shared with the Lambda if it ever needs to enforce server-side. */
export const CERTIFICATION_SETTINGS_KEY = 'creyenteone:certification-settings:v1';

export interface CertificationSettings {
  /**
   * The configured approver's canonical email, or '' when nobody is configured.
   *
   * Empty rather than undefined so "nobody is configured" has one spelling; a field that could
   * be either makes every reader handle two cases that mean the same thing.
   */
  certificationAdminEmail: string;
  updatedBy?: string;
  updatedAt?: string;
}

export const defaultCertificationSettings: CertificationSettings = {
  certificationAdminEmail: '',
};

let client: any = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};

const listOrgConfigQuery = /* GraphQL */ `
  query ListOrgConfig {
    listOrgConfig { configKey value updatedBy updatedAt }
  }
`;

const putOrgConfigMutation = /* GraphQL */ `
  mutation PutOrgConfig($configKey: String!, $value: AWSJSON!, $updatedBy: String) {
    putOrgConfig(configKey: $configKey, value: $value, updatedBy: $updatedBy) { configKey }
  }
`;

/**
 * A process-lifetime cache, refreshed on demand.
 *
 * The approval screens ask "may this person decide?" on every render of a queue row. That must
 * not become a query per row, and the answer changes on the scale of somebody editing Settings.
 * `forceRefresh` is what the Settings page itself uses after saving, so it never shows a stale
 * copy of the value it just wrote.
 */
let cached: CertificationSettings | null = null;
let inFlight: Promise<CertificationSettings> | null = null;

const parse = (raw: unknown): CertificationSettings => {
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const email = canonicalizeEmail(String((value as any)?.certificationAdminEmail || ''));
    return {
      certificationAdminEmail: email,
      updatedBy: (value as any)?.updatedBy,
      updatedAt: (value as any)?.updatedAt,
    };
  } catch {
    /*
     * An unreadable setting means NOBODY is configured, which is the safe direction.
     *
     * The alternative — throwing — would take down the approval queue for the existing admins
     * too, over a value that only ever ADDS an approver. Failing closed on the extra grant while
     * leaving the built-in one intact is the behaviour that degrades correctly.
     */
    return { ...defaultCertificationSettings };
  }
};

export async function loadCertificationSettings(
  options: { forceRefresh?: boolean } = {}
): Promise<CertificationSettings> {
  if (!options.forceRefresh && cached) return cached;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const result: any = await getClient().graphql({
        query: listOrgConfigQuery,
        authMode: 'userPool' as const,
      });
      const docs = ('data' in result ? result.data : null)?.listOrgConfig;
      const found = Array.isArray(docs)
        ? docs.find((d: any) => String(d?.configKey) === CERTIFICATION_SETTINGS_KEY)
        : undefined;
      cached = found ? parse(found.value) : { ...defaultCertificationSettings };
      return cached;
    } catch (error) {
      /*
       * A failed read must not grant the extra approver, and must not revoke the built-in ones.
       *
       * Returning the default does exactly that: canApproveCertifications falls back to the role
       * check alone, so finance and super admins carry on and nobody is added on the strength of
       * a value that could not be read.
       */
      console.warn('[CertificationSettings] could not read settings:', error);
      return { ...defaultCertificationSettings };
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/**
 * Save the configured approver. An empty email clears the configuration.
 *
 * Clearing is a first-class operation, not an edge case: "remove the Certification Admin" is
 * something Settings has to be able to express, and the only honest representation of it is an
 * empty value rather than a deleted document nothing can then read.
 */
export async function saveCertificationSettings(
  settings: CertificationSettings,
  updatedBy: string
): Promise<CertificationSettings> {
  const value: CertificationSettings = {
    certificationAdminEmail: canonicalizeEmail(settings.certificationAdminEmail || ''),
    updatedBy,
    updatedAt: new Date().toISOString(),
  };

  await getClient().graphql({
    query: putOrgConfigMutation,
    variables: {
      configKey: CERTIFICATION_SETTINGS_KEY,
      value: JSON.stringify(value),
      updatedBy,
    },
    authMode: 'userPool' as const,
  });

  cached = value;
  return value;
}

/** Drop the cache. For tests, and for a screen that knows the value has changed elsewhere. */
export function clearCertificationSettingsCache(): void {
  cached = null;
  inFlight = null;
}
